// The run-options block (UX slice S3): the backend's `data.run_options`, read defensively (it comes
// from the network and from saved tickets), plus the helpers the block and the chat share. The
// backend builds every word of it (export_design_workflow._run_options_for); nothing here writes copy
// beyond the save option's saved / changed states, which only the client knows about.
// S12 R-4 (board 12) shows it in three steps (save / continue / new, then what next, then live or in the
// background) from the same payload; blocks stored before S12 still read and render.
//
// Not called exportDesignRunOptions.ts: on a case-insensitive file system that would shadow the
// component ExportDesignRunOptions.tsx (plan §0.5).

export interface RunOptionStep {
  id: string;
  text: string;
  note?: string;
  done?: boolean;
}

export interface RunOptionLive {
  label: string;
  description: string;
  prompt: string;
}

export interface RunOptionChoice {
  /** The target regime ("EAR99", "EAR", free text), or null for "no design changes". */
  target: string | null;
  label: string;
  /** "suggested" / "you asked for this", shown next to the label. */
  why?: string | null;
  steps: RunOptionStep[];
  typical?: string;
  live: RunOptionLive;
  /** "Run in the background" for this choice (S8b): its label, explanation and the sentence sent. */
  background?: RunOptionLive;
  /** The plan on one line, for the answered (collapsed) block. */
  summary: string;
  selected: boolean;
}

export interface RunOptionAction {
  id: 'live' | 'save' | 'new' | 'background' | 'continue';
  label?: string;
  description?: string;
  /** Blocks stored before S12 only (D-30 dropped the badge). */
  recommended?: boolean;
}

export type RunOptionMoreKind = 'licensing' | 'what_if' | 'test_changes' | 'other_target';

export interface RunOptionInput {
  /** The field's label, above it. */
  label?: string;
  placeholder: string;
  help?: string;
  /** `{country}` (licensing) or `{value}` is replaced with what was typed. */
  prompt_template: string;
}

export interface RunOptionMore {
  label: string;
  kind?: RunOptionMoreKind;
  /** Its name once picked ("Licensing for a country"). */
  title?: string;
  description?: string;
  /** What will happen, in plain words. */
  steps?: string[];
  typical?: string;
  /** Sent as the user's message. */
  prompt?: string;
  /** Put in the composer, not sent. */
  composer?: string;
  /** Asked for before it can run; the template's placeholder is replaced with what was typed. */
  input?: RunOptionInput;
}

/** "Another target…" (S12 R-14, board 14 C2): step 3 with a box, then live or background like a target button. */
export interface RunOptionsCustomTarget {
  label: string;
  title: string;
  steps: string[];
  typical?: string;
  /** The box; its template is the live sentence (the backend's `live.prompt_template`). */
  input: RunOptionInput;
  /** The step once a target is typed (`{value}` = the target). */
  stepTemplate: string;
  live: { label: string; description: string };
  background: { label: string; description: string; prompt_template: string };
}

export interface RunOptionsPayload {
  question: string;
  sub: string;
  checkpoint: boolean;
  choices: RunOptionChoice[];
  /** The choice the chat already asked for: what "Continue" does (D-29), or null. */
  requested: number | null;
  options: RunOptionAction[];
  more: RunOptionMore[];
  /** Absent on blocks from before S12 R-14: "Another target…" then fills the message box. */
  customTarget?: RunOptionsCustomTarget;
}

const isString = (value: unknown): value is string => typeof value === 'string';

const readStep = (raw: any): RunOptionStep | null => {
  if (!raw || !isString(raw.id) || !isString(raw.text) || !raw.text.trim()) return null;
  const step: RunOptionStep = { id: raw.id, text: raw.text };
  if (isString(raw.note) && raw.note.trim()) step.note = raw.note;
  if (raw.done === true) step.done = true;
  return step;
};

const readChoice = (raw: any): RunOptionChoice | null => {
  if (!raw || !isString(raw.label) || !raw.live || !isString(raw.live.prompt) || !raw.live.prompt.trim()) {
    return null;
  }
  const steps = (Array.isArray(raw.steps) ? raw.steps : [])
    .map(readStep)
    .filter((s: RunOptionStep | null): s is RunOptionStep => s !== null);
  if (!steps.length) return null;
  const bg = raw.background;
  const background: RunOptionLive | undefined =
    bg && isString(bg.prompt) && bg.prompt.trim()
      ? {
          label: isString(bg.label) && bg.label.trim() ? bg.label : 'Run in the background',
          description: isString(bg.description) ? bg.description : '',
          prompt: bg.prompt,
        }
      : undefined;
  return {
    target: isString(raw.target) ? raw.target : null,
    label: raw.label,
    why: isString(raw.why) ? raw.why : null,
    steps: steps,
    typical: isString(raw.typical) ? raw.typical : undefined,
    live: {
      label: isString(raw.live.label) ? raw.live.label : 'Walk me through it live',
      description: isString(raw.live.description) ? raw.live.description : '',
      prompt: raw.live.prompt,
    },
    ...(background ? { background: background } : {}),
    summary: isString(raw.summary) ? raw.summary : '',
    selected: raw.selected === true,
  };
};

const ACTION_IDS = new Set(['live', 'save', 'new', 'background', 'continue']);
const MORE_KINDS = new Set(['licensing', 'what_if', 'test_changes', 'other_target']);
const TEMPLATE_SLOTS = ['{country}', '{value}'];

const readMore = (raw: any): RunOptionMore | null => {
  if (!raw || !isString(raw.label) || !raw.label.trim()) return null;
  const item: RunOptionMore = { label: raw.label };
  if (isString(raw.kind) && MORE_KINDS.has(raw.kind)) item.kind = raw.kind as RunOptionMoreKind;
  if (isString(raw.title) && raw.title.trim()) item.title = raw.title;
  if (isString(raw.description) && raw.description.trim()) item.description = raw.description;
  if (isString(raw.typical) && raw.typical.trim()) item.typical = raw.typical;
  const steps = (Array.isArray(raw.steps) ? raw.steps : []).filter((s: unknown) => isString(s) && s.trim());
  if (steps.length) item.steps = steps;
  if (isString(raw.prompt) && raw.prompt.trim()) return { ...item, prompt: raw.prompt };
  if (isString(raw.composer)) return { ...item, composer: raw.composer };
  const input = raw.input;
  if (input && isString(input.prompt_template) && TEMPLATE_SLOTS.some((slot) => input.prompt_template.includes(slot))) {
    return {
      ...item,
      input: {
        ...(isString(input.label) && input.label.trim() ? { label: input.label } : {}),
        placeholder: isString(input.placeholder) ? input.placeholder : '',
        ...(isString(input.help) && input.help.trim() ? { help: input.help } : {}),
        prompt_template: input.prompt_template,
      },
    };
  }
  return null;
};

/** What an input item sends: its template with what was typed in place of the slot. */
export const fillRunPrompt = (template: string, value: string): string =>
  TEMPLATE_SLOTS.reduce((text, slot) => text.split(slot).join(value.trim()), template);

const hasSlot = (template: unknown): template is string =>
  isString(template) && TEMPLATE_SLOTS.some((slot) => template.includes(slot));

const readCustomTarget = (raw: any): RunOptionsCustomTarget | undefined => {
  if (!raw || !isString(raw.title) || !raw.input || !raw.live || !hasSlot(raw.live.prompt_template)) return undefined;
  if (!hasSlot(raw.step_template) || !raw.background || !hasSlot(raw.background.prompt_template)) return undefined;
  const text = (value: unknown, fallback: string): string => (isString(value) && value.trim() ? value : fallback);
  return {
    label: text(raw.label, 'Another target…'),
    title: raw.title,
    steps: (Array.isArray(raw.steps) ? raw.steps : []).filter((step: unknown) => isString(step) && step.trim()),
    ...(isString(raw.typical) && raw.typical.trim() ? { typical: raw.typical } : {}),
    input: {
      ...(isString(raw.input.label) && raw.input.label.trim() ? { label: raw.input.label } : {}),
      placeholder: isString(raw.input.placeholder) ? raw.input.placeholder : '',
      ...(isString(raw.input.help) && raw.input.help.trim() ? { help: raw.input.help } : {}),
      // The box's sentence is the live one ("Walk me through it live: find what would move it to {value}.").
      prompt_template: raw.live.prompt_template,
    },
    stepTemplate: raw.step_template,
    live: {
      label: text(raw.live?.label, 'Walk me through it live'),
      description: text(raw.live?.description, "Stay here and watch it work. I'll stop when it's done so you can steer."),
    },
    background: {
      label: text(raw.background.label, 'Run in the background'),
      description: text(raw.background.description, ''),
      prompt_template: raw.background.prompt_template,
    },
  };
};

/** The typed target as a choice, so it runs live or in the background exactly like a target button. */
export const customTargetChoice = (custom: RunOptionsCustomTarget, value: string): RunOptionChoice => {
  const target = value.trim();
  return {
    target: target,
    label: target,
    why: null,
    steps: [{ id: 'design_changes', text: fillRunPrompt(custom.stepTemplate, target) }],
    ...(custom.typical ? { typical: custom.typical } : {}),
    live: { ...custom.live, prompt: fillRunPrompt(custom.input.prompt_template, target) },
    background: {
      label: custom.background.label,
      description: custom.background.description,
      prompt: fillRunPrompt(custom.background.prompt_template, target),
    },
    summary: `Design changes to ${target}`,
    selected: false,
  };
};

/** `data.run_options`, or undefined when absent or unusable (the chat then shows today's chips). */
export const readRunOptions = (input: unknown): RunOptionsPayload | undefined => {
  const raw = input as any;
  if (!raw || typeof raw !== 'object' || !isString(raw.question)) return undefined;
  const read: (RunOptionChoice | null)[] = (Array.isArray(raw.choices) ? raw.choices : []).map(readChoice);
  const choices = read.filter((c: RunOptionChoice | null): c is RunOptionChoice => c !== null);
  const more = (Array.isArray(raw.more) ? raw.more : [])
    .map(readMore)
    .filter((m: RunOptionMore | null): m is RunOptionMore => m !== null);
  if (!choices.length && !more.length) return undefined;
  const options = (Array.isArray(raw.options) ? raw.options : []).filter(
    (o: any) => o && ACTION_IDS.has(o.id),
  ).map((o: any): RunOptionAction => ({
    id: o.id,
    label: isString(o.label) ? o.label : undefined,
    description: isString(o.description) ? o.description : undefined,
    recommended: o.recommended === true,
  }));
  // `requested` indexes the choices as sent; an unusable one dropped before it would shift the rest.
  const requestedChoice =
    typeof raw.requested === 'number' && Number.isInteger(raw.requested) ? read[raw.requested] : null;
  const requested = requestedChoice ? choices.indexOf(requestedChoice) : null;
  return {
    question: raw.question,
    sub: isString(raw.sub) ? raw.sub : '',
    checkpoint: raw.checkpoint === true,
    choices: choices,
    requested: requested,
    options: options,
    more: more,
    ...(readCustomTarget(raw.custom_target) ? { customTarget: readCustomTarget(raw.custom_target) } : {}),
  };
};

/** The same normalisation the backend matches a click with (_normalize_run_prompt). */
export const normalizeRunPrompt = (text: string): string =>
  (text || '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/[.!]+$/, '');

export type RunOptionMode = 'live' | 'background';

/**
 * Which choice the user ran from this block, and how: the one whose live (or background) sentence is
 * the next thing they said. Null when they did something else (typed a reply, or nothing yet).
 */
export const answeredRun = (
  runOptions: RunOptionsPayload,
  laterUserTexts: string[],
): { choice: number; mode: RunOptionMode } | null => {
  const next = laterUserTexts[0];
  if (!isString(next)) return null;
  const said = normalizeRunPrompt(next);
  for (let i = 0; i < runOptions.choices.length; i += 1) {
    const choice = runOptions.choices[i];
    if (normalizeRunPrompt(choice.live.prompt) === said) return { choice: i, mode: 'live' };
    if (choice.background && normalizeRunPrompt(choice.background.prompt) === said) {
      return { choice: i, mode: 'background' };
    }
  }
  return null;
};

/** The index of the choice run from this block (live or in the background), or -1. */
export const answeredChoiceIndex = (
  runOptions: RunOptionsPayload,
  laterUserTexts: string[],
): number => answeredRun(runOptions, laterUserTexts)?.choice ?? -1;

export type RunOptionsSaveState = 'unsaved' | 'saved' | 'changed';

/** The save option as it reads now: saving keeps the block open, so its label follows the ticket. */
export const saveOptionCopy = (
  action: RunOptionAction | undefined,
  saveState: RunOptionsSaveState,
): { label: string; description: string } => {
  if (saveState === 'saved') {
    // S12 R-9: the label itself is the way to the ticket.
    return { label: 'View Saved', description: 'Open it any time, or carry on below.' };
  }
  if (saveState === 'changed') {
    return {
      label: 'Update the saved ticket',
      description: "Add what's new since you saved it to the same ticket. Nothing else runs.",
    };
  }
  return {
    label: action?.label || 'Save this result',
    description:
      action?.description ||
      'Save the classification and the specs behind it as a ticket in Saved Work. Nothing else runs.',
  };
};
